import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import Path from 'routes/Path';
import { useQueryParams } from 'hooks/useQueryParams';
import { FreecodeLoading } from 'components/FreecodePage/FreecodeLoading/FreecodeLoading';
import { identifiers, editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import FreecodeSummary from 'components/FreecodePage/FreecodeTabs/FreecodeDescription/FreecodeSummary';
import FreecodePage from 'components/FreecodePage/FreecodePage';
import { useFreecodeContextLayout, useFreecodeContextData } from 'hooks/useContext';
import InformationModal from 'components/Modals/informationModal';
import FreecodeLayout from 'components/FreecodePage/FreecodeLayout/FreecodeLayout';
import FreecodePreview from 'components/FreecodePage/FreecodeTabs/Creator/FreecodePreview/FreecodePreview';
import FreecodeEditorImplementation from 'components/FreecodePage/FreecodeTabs/FreecodeCodeEditor/FreecodeEditorImplementation';
import FreecodeApiSettings from 'components/FreecodePage/FreecodeTabs/Creator/FreecodeApiSettings/FreecodeApiSettings';
import FreecodeSettings from 'components/FreecodePage/FreecodeTabs/Creator/FreecodeSettings/FreecodeSettings';
import FreecodeGraphics from 'components/FreecodePage/FreecodeTabs/Creator/FreecodeGraphics/FreecodeGraphics';
import FreecodeFlow from 'components/FreecodePage/FreecodeTabs/FreecodeDescription/FreecodeFlow';
import FreecodeSkeletonEditor from 'components/FreecodePage/FreecodeTabs/Creator/FreecodeSkeletonEditor/FreecodeSkeletonEditor';
import useCreatorRunFunction from './useCreatorRunFunction';
import useCreatorLoadFunction from './useCreatorLoadFunction';
import useCreatorSaveFunction from './useCreatorSaveFunction';
import { categories } from 'constants/tests';
import { ErrorText } from './styles';

const HEADER_LINKS = [{ link: Path.customTests, label: 'Create Page' }];

export const FreecodeCreatorContext = React.createContext();

const getProcessedServerError = (newServerError) => {
  if (!newServerError?.traceback) return;
  let error = { ...newServerError };
  let fullTrace = String(error.traceback);
  let traceback = '';
  if (fullTrace.length) {
    if (fullTrace.startsWith('Traceback')) {
      traceback = fullTrace.substring(0, fullTrace.indexOf('\n'));
    }
    if (fullTrace.includes('challenge.py')) {
      traceback = traceback.concat(fullTrace.substring(fullTrace.search(/\n.*\/challenge\.py/)));
    }
    if (traceback.length) {
      error.traceback = <ErrorText>{traceback}</ErrorText>;
      error.message = error.message || 'An error occurred';
      return error;
    }
  }
};

export default function FreecodeCreator() {
  const { testId } = useParams();
  const { testImplId } = useQueryParams();

  const [serverError, setServerError] = useState(undefined);

  const updateServerError = useCallback(
    (newServerError) => setServerError(getProcessedServerError(newServerError)),
    []
  );

  const { setEditorType } = useFreecodeContextLayout();
  const { test, testImpl } = useFreecodeContextData();
  const { implVersions, loading, error } = useCreatorLoadFunction(testId, testImplId);
  const { save, saving } = useCreatorSaveFunction(updateServerError);
  useCreatorRunFunction(save, updateServerError);

  useEffect(() => {
    setEditorType(editorTypes.creator);
  }, [setEditorType]);

  const creatorComponents = useMemo(() => {
    if (loading) return {};
    let creatorComponents = {
      [identifiers.summary]: <FreecodeSummary />,
      [identifiers.implementation]: <FreecodeEditorImplementation />,
      [identifiers.apiSettings]: <FreecodeApiSettings />,
      [identifiers.graphicsSettings]: <FreecodeGraphics />,
      [identifiers.settings]: <FreecodeSettings />,
      [identifiers.preview]: <FreecodePreview />,
    };
    if (testImpl.isFlow) creatorComponents[identifiers.flow] = <FreecodeFlow />;
    if (test.category === categories.CODE.category && testImpl.enableSkeletonEditing) {
      creatorComponents[identifiers.enableSkeletonEditing] = <FreecodeSkeletonEditor />;
    }
    return creatorComponents;
  }, [loading, testImpl?.enableSkeletonEditing, test?.category, testImpl?.isFlow]);

  return (
    <FreecodeCreatorContext.Provider value={{ implVersions, save, saving }}>
      <FreecodePage headerLinks={HEADER_LINKS}>
        <FreecodeLoading
          signInRequired
          loading={loading}
          errors={[error]}
          errorLinkText="All Custom Implementations"
          errorLink={Path.customTests}
        />
        {!loading && <FreecodeLayout parentComponents={creatorComponents} />}
        {serverError && (
          <InformationModal
            title={serverError.message}
            text={serverError.traceback}
            closeModal={() => setServerError(undefined)}
          />
        )}
      </FreecodePage>
    </FreecodeCreatorContext.Provider>
  );
}
